import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';

interface BasicInfoProps {
  data: {
    ahgModel: string;
    supplier: string;
    supplierModel: string;
    refrigerantCode: string;
    source: string;
    target: string;
    ahgPrice: string;
    applications: string;
  };
  onChange: (field: string, value: string) => void;
}

const BasicInfoSection: React.FC<BasicInfoProps> = ({ data, onChange }) => {
  const applicationOptions = ['DHW', 'HHW', 'High Temp', 'DHW/HHW'];
  
  const applicationTooltip = `
    High Temp: 2 refrigerant circuits, such as R32/R515B or R410A/R134A
    DHW: Usually only R744 units, which cannot handle the lower ΔT required for HHW or high temp or cannot handle low ambients (eg: R134A units)
    HHW: Units which can handle lower ΔT than transcritical units but cannot reach 60 °C outlet temperature
    DHW/HHW: eg: R290
    
    DHW heat pumps can output at least 60 °C. 
    HHW heat pumps can output in the 30-50 °C range and accept lower ambient temperatures (<-10 °C) without appreciable drop in capacity. 
    High temp units can output 75 °C at 7 °C ambient and handle a 5 K ΔT on the water side and usually have two refrigerant circuits.
  `;

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h5">1. Basic Information</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Paper variant="outlined" className="table-container" sx={{ p: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                label="AHG Model"
                value={data.ahgModel}
                onChange={(e) => onChange('ahgModel', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="Automatic Heating's model number"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                required
                label="Supplier"
                value={data.supplier}
                onChange={(e) => onChange('supplier', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="Supplier name (eg: Itomic, Daikin)"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                required
                label="Supplier Model"
                value={data.supplierModel}
                onChange={(e) => onChange('supplierModel', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="Supplier model number"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                required
                label="Refrigerant Code"
                value={data.refrigerantCode}
                onChange={(e) => onChange('refrigerantCode', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                helperText="Eg: R134A, R744, R290, R32/R515B"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                disabled
                label="Source"
                value={data.source}
                fullWidth
                margin="normal"
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                disabled
                label="Target"
                value={data.target}
                fullWidth
                margin="normal"
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                required
                label="AHG Price"
                value={data.ahgPrice}
                onChange={(e) => onChange('ahgPrice', e.target.value)}
                fullWidth
                margin="normal"
                className="blue-highlight"
                size="small"
                type="number"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" className="blue-highlight">
                <InputLabel id="applications-label">Application(s)</InputLabel>
                <Select
                  labelId="applications-label"
                  value={data.applications}
                  label="Application(s)"
                  onChange={(e) => onChange('applications', e.target.value)}
                  size="small"
                  required
                >
                  <MenuItem value="">
                    <em>Select application</em>
                  </MenuItem>
                  {applicationOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <Tooltip title={applicationTooltip} placement="bottom-start">
                  <Typography 
                    variant="caption" 
                    display="flex" 
                    alignItems="center" 
                    sx={{ mt: 0.5, cursor: 'help' }}
                  >
                    <InfoIcon fontSize="small" sx={{ mr: 0.5 }} />
                    Click for more information on applications
                  </Typography>
                </Tooltip>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
};

export default BasicInfoSection;